import React, { useEffect, useState } from "react";
import { Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import Loader from "components/Loader";
import HeaderButtons from "components/Button/HeaderButtons";
import BackButton from "components/Button/BackButton";
import { useToaster } from "contexts/toasterContext";
import { getApiErrorMessage } from "util/errorMessage";
import feedbackManager from "services/Feedback/feedbackManager";
import { Feedback, getQuarterMonths, KraArea } from "services/Feedback/feedbackInterface";
import ManagerFeedbackForm, { FeedbackTarget, ManagerFeedbackMode } from "./ManagerFeedbackForm";

/**
 * Manager / admin feedback as a routed SCREEN.
 *
 * CHANGE: this was a `GenericModal`. Once approving a sheet could carry goal
 * rows, the dialog hit exactly the problem the goal-sheet screen was built to
 * fix — a form taller than the viewport with its Save button below the fold.
 * The reviewer side now gets the same treatment as the employee side.
 *
 *   approve → /admin/feedback/review/approve/:selfFeedbackId
 *   create  → /admin/feedback/review/new/:userId
 *   edit    → /admin/feedback/review/:feedbackId
 *
 * NOTE: a thin shell. All the form behaviour stays in `ManagerFeedbackForm`,
 * which is unchanged in shape — this only resolves the route into the props it
 * already takes, so there is no second copy of the form to keep in step.
 */
export interface ManagerFeedbackPageProps {
  mode: ManagerFeedbackMode;
}

const LIST_PATH = "/admin/feedback";

const TITLES: Record<ManagerFeedbackMode, string> = {
  approve: "Approve with feedback",
  create: "Add feedback",
  edit: "Edit feedback",
};

const ManagerFeedbackPage: React.FC<ManagerFeedbackPageProps> = ({ mode }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams<{ selfFeedbackId?: string; feedbackId?: string; userId?: string }>();
  const { addToast } = useToaster();

  /**
   * Both usually arrive through router state, so opening from a card costs no
   * round trip. A refresh or a pasted link has neither, so they are fetched.
   */
  const passed = (location.state as { feedback?: Feedback; employee?: FeedbackTarget } | null) ?? null;

  const [record, setRecord] = useState<Feedback | null>(passed?.feedback ?? null);
  const [employee, setEmployee] = useState<FeedbackTarget | null>(passed?.employee ?? null);
  const [areas, setAreas] = useState<KraArea[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");

  const idParam = mode === "approve" ? params.selfFeedbackId : params.feedbackId;

  // Areas feed the goal-row dropdowns; only active ones are selectable.
  useEffect(() => {
    feedbackManager
      .getKraAreas(false)
      .then((response) => setAreas(Array.isArray(response.data) ? response.data : []))
      .catch((error) => addToast(getApiErrorMessage(error, "Failed to load KRA areas"), "error"));
  }, [addToast]);

  useEffect(() => {
    const needsRecord = mode !== "create" && !record;
    const needsEmployee = !employee;
    if (!needsRecord && !needsEmployee) return;

    let cancelled = false;
    setLoading(true);

    const hydrate = async () => {
      try {
        let loaded = record;

        if (needsRecord && idParam) {
          const response = await feedbackManager.getFeedback(Number(idParam));
          loaded = response.data;
          if (!cancelled) setRecord(loaded);
        }

        if (needsEmployee) {
          if (loaded) {
            // Every feedback names who it is ABOUT, so no extra call is needed.
            if (!cancelled) setEmployee({ userId: loaded.userId, name: loaded.userName });
          } else if (params.userId) {
            const response = await feedbackManager.getUserFeedback({
              userId: Number(params.userId),
            });
            const summary = response.data.summary;
            if (!cancelled) setEmployee({ userId: summary.userId, name: summary.name });
          }
        }
      } catch (error) {
        if (!cancelled) setLoadError(getApiErrorMessage(error, "Failed to open this feedback."));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void hydrate();
    return () => {
      cancelled = true;
    };
    // NOTE: `record` and `employee` are deliberately NOT dependencies — they
    // are what this effect fills in, so depending on them would re-run it the
    // moment it succeeds. The route params are the real inputs.
  }, [mode, idParam, params.userId]);

  /** Back to the roll-up with this employee reopened, not the bare list. */
  const goBack = () =>
    navigate(LIST_PATH, { state: employee ? { selected: employee } : undefined });

  const handleSaved = (_feedback: Feedback, message: string) => {
    addToast(message, "success");
    goBack();
  };

  // ── Guards ────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex min-h-[240px] items-center justify-center">
        <Loader />
      </div>
    );
  }

  /**
   * A sheet someone else already approved, or a record that is not what this
   * mode expects, cannot be acted on — the server would refuse every save.
   */
  if (mode === "approve" && record && (record.kind !== "SELF" || record.status !== "SUBMITTED")) {
    return <Navigate to={LIST_PATH} replace />;
  }
  if (mode === "edit" && record && (record.kind !== "MANAGER" || !record.editable)) {
    return <Navigate to={LIST_PATH} replace />;
  }

  if (loadError || !employee || (mode !== "create" && !record)) {
    return (
      <div className="p-4">
        <div className="rounded-lg bg-red-50 px-3 py-2 text-sm font-medium text-error dark:bg-red-900/20">
          {loadError || "This feedback could not be opened."}
        </div>
        <HeaderButtons type="button" variant="tertiary" size="small" className="mt-3" onClick={goBack}>
          Back to review
        </HeaderButtons>
      </div>
    );
  }

  // ── Render ────────────────────────────────────────────────────────────────

  /*
   * CHANGE: same fix as GoalSheetFormPage, which this screen mirrors. On
   * desktop the shell hands a page EXACTLY 100vh — the top nav sits inside the
   * left sidebar column, not above the page column — so h-[calc(100vh-75px)]
   * left a 75px band of dead white below the pinned action bar. h-full fills
   * the box it is given, and the plan banner is a flex sibling of that box, so
   * it is already accounted for without reading BannerContext.
   */
  return (
    <div
      className="flex h-full flex-col bg-white text-slate-900 transition-colors duration-300 dark:bg-gray-800 dark:text-white"
    >
      <div className="shrink-0 border-b border-gray-200 px-3 py-2.5 dark:border-gray-700 sm:px-4">
        <div className="flex min-w-0 items-center gap-2.5">
          <BackButton onClick={goBack} label="Back to reviews" />
          <div className="min-w-0">
            <h1 className="truncate text-base font-semibold">{TITLES[mode]}</h1>
            <p className="truncate text-xs text-gray-400 dark:text-gray-500">
              About {employee.name}
              {record
                ? ` · ${record.period}${getQuarterMonths(record.quarter) ? ` (${getQuarterMonths(record.quarter)})` : ""
                }`
                : ""}
            </p>
          </div>
        </div>
      </div>

      {/* The form keeps its own actions at the end; with the goal grid off by
          default the whole thing fits without scrolling. */}
      <div className="min-h-0 flex-1 overflow-y-auto px-3 py-3 sm:px-4">
        {/* CHANGE: the 1024px cap is gone. It left most of the screen empty
            while the goal rows underneath still needed every pixel, and it made
            this screen the only one in the module that did not fill its space. */}
        <div>
          <ManagerFeedbackForm
            mode={mode}
            employee={employee}
            selfFeedback={mode === "approve" ? record : null}
            initialValue={mode === "edit" ? record : null}
            areas={areas}
            onSaved={handleSaved}
            onClose={goBack}
          />
        </div>
      </div>
    </div>
  );
};

export default ManagerFeedbackPage;
